import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** Exactly two themes — no system/auto option. */
export type ThemePreference = "light" | "dark";

const STORAGE_KEY = "mindforge.theme";
const SUPPORTED: readonly ThemePreference[] = ["light", "dark"];
const DEFAULT_THEME: ThemePreference = "dark";

export interface ThemeContextValue {
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): ThemePreference {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && (SUPPORTED as readonly string[]).includes(stored)) {
      return stored as ThemePreference;
    }
  } catch {
    // localStorage unavailable — fall through to default
  }
  return DEFAULT_THEME;
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Test override — bypasses stored value. */
  initialPreference?: ThemePreference;
}

export function ThemeProvider({ children, initialPreference }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initialPreference ?? readStored(),
  );

  // Apply to <html data-theme="..."> and persist the choice.
  useEffect(() => {
    document.documentElement.dataset.theme = preference;
    try {
      window.localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // ignore (private browsing)
    }
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
  }, []);

  const ctx = useMemo<ThemeContextValue>(
    () => ({ preference, setPreference }),
    [preference, setPreference],
  );

  return <ThemeContext.Provider value={ctx}>{children}</ThemeContext.Provider>;
}
